/**
 * sedFingerprint — Cost Phase P4 (strategy §2b/§2c/§2e).
 *
 * Unifies the two parallel hash conventions the codebase carries (the FNV-1a
 * string hash in `useIntentIntelligence.hashInferenceContext` and the
 * file-bytes `contentHash` on `SiteDocument`) into ONE per-section `inputHash`,
 * then uses those fingerprints to compute a CONSERVATIVE dirty set for an
 * incremental SED rebuild.
 *
 * The per-section `inputHash` is FNV-1a over a STABLE, order-independent
 * serialization of:
 *   - the provider-input bundle (coords + provider config) for the section's
 *     providers,
 *   - the contentHashes of the docs linked via the section's docRequirements,
 *   - the values of the section's conditioning intent fields,
 *   - the EXTRACTION_MODEL_VERSION,
 *   - the upstream sections' OWN inputHashes (so a transitive input change
 *     propagates through the hash too — belt-and-braces with the BFS closure).
 *
 * CARDINAL RULE (strategy §2c): NEVER under-recompute. A stale-but-stamped-
 * current section is the one failure mode that silently degrades truth. So the
 * dirty-set computation is CONSERVATIVE — when in doubt, mark DIRTY:
 *   - a section with no prior fingerprint ⇒ DIRTY,
 *   - any input that is unknown / unhashable ⇒ DIRTY,
 *   - a section whose model version drifted ⇒ DIRTY,
 *   - the transitive closure over upstreamSections ⇒ DIRTY.
 *
 * Over-recompute is acceptable; under-recompute is forbidden.
 */
import type {
  SectionFingerprint,
  SedSectionId,
  SiteEvidenceDocument,
} from '../../types/siteEvidence';
import { SED_SECTION_IDS } from '../../types/siteEvidence';
import type { ProjectIntent, SiteDocument } from '../../types/siteRag';
import { EXTRACTION_MODEL_VERSION } from '../../services/knowledge/extractionCache';
import {
  SED_DEPENDENCY_GRAPH,
  SED_DOWNSTREAM_DEPENDENTS,
  isProviderFedSection,
  type SedSectionDeps,
} from './sedDependencyGraph';

/* ── FNV-1a (unified) ──────────────────────────────────────────────────
 *
 * Byte-for-byte the same FNV-1a 32-bit → hex used by
 * `useIntentIntelligence.hashInferenceContext`, lifted here so the dirty-set
 * layer has ONE shared hash vocabulary instead of two parallel ones.
 */
export function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}

/**
 * Sentinel emitted into a section's hash input when an input is present in the
 * dependency graph but cannot be resolved/hashed from the live inputs. Its
 * presence forces the live hash to differ from any stamped one (which never
 * contains the sentinel for that axis), so the section is conservatively dirty.
 */
const UNKNOWN = '\u0000UNKNOWN\u0000';

/**
 * Live inputs the fingerprint reads. Everything is optional so a sparse caller
 * (e.g. the server path that lacks doc-aware confidence) degrades to "unknown"
 * → conservative dirty, never silently fresh.
 */
export interface SedFingerprintInputs {
  /** Site coordinates — the provider-input bundle's spatial key. */
  coords?: { lat?: number; lng?: number } | null;
  /**
   * Per-provider config blob (api keys excluded — only config that changes the
   * RESULT, e.g. a grid-cell granularity). Keyed by provider id. Optional;
   * absent ⇒ providers contribute only the coords axis.
   */
  providerConfig?: Record<string, unknown> | null;
  /** Live project intent — the source of the intent-field axis. */
  intent?: ProjectIntent | null;
  /**
   * The site's documents (or the report library), keyed for contentHash
   * lookup. Used together with each section's linked doc ids.
   */
  documents?: ReadonlyArray<SiteDocument> | null;
  /** Server-owned workforce/screening/research revision for economicEvidence. */
  economicEvidenceRevision?: string | null;
}

/** Deterministic, order-independent serialization of a JSON-ish value. */
function stableSerialize(value: unknown): string {
  if (value === undefined) return 'u';
  if (value === null) return 'n';
  const t = typeof value;
  if (t === 'number') return Number.isFinite(value as number) ? `#${value}` : UNKNOWN;
  if (t === 'boolean') return value ? 'T' : 'F';
  if (t === 'string') return `s:${value}`;
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }
  if (t === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${k}=${stableSerialize(obj[k])}`).join(',')}}`;
  }
  // Functions / symbols — unhashable → unknown (conservative).
  return UNKNOWN;
}

/** Read a (possibly dotted) intent field path. Missing path ⇒ undefined. */
function readIntentField(intent: ProjectIntent | null | undefined, path: string): unknown {
  if (!intent) return undefined;
  if (!path.includes('.')) return (intent as unknown as Record<string, unknown>)[path];
  let cur: unknown = intent;
  for (const seg of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/**
 * The linked doc ids for a section, read off the live SED slot
 * (`linkedDocumentIds` ∪ `linkedDocumentRefs`). The dirty-set hashes the
 * contentHashes of these docs so a re-uploaded (re-hashed) doc dirties the
 * sections it feeds.
 */
function linkedDocIdsForSection(
  sed: SiteEvidenceDocument | null | undefined,
  section: SedSectionId,
): string[] {
  if (!sed) return [];
  const slot = (sed as unknown as Record<string, unknown>)[section] as
    | { linkedDocumentIds?: string[]; linkedDocumentRefs?: Array<{ docId?: string }> }
    | undefined;
  if (!slot) return [];
  const ids = new Set<string>();
  for (const id of slot.linkedDocumentIds ?? []) ids.add(id);
  for (const ref of slot.linkedDocumentRefs ?? []) if (ref.docId) ids.add(ref.docId);
  return [...ids].sort();
}

/**
 * Null-data guard (data-starvation fix). True when a section's slot on the
 * given SED has been COMPILED but carries no usable data — i.e. the slot exists
 * and has a `status` (so it is a real compiled `EvidenceSection`, not an
 * uncompiled / stub slot), AND either:
 *   - its `status` is `'unavailable'`, OR
 *   - its `.data` is null / undefined, OR
 *   - its `.data` is an empty array.
 *
 * This is the real data-starvation condition: a provider-fed slot the provider
 * never filled (e.g. it returned `unavailable`), leaving `data: null`. The
 * incremental "topup-no-upgrades" no-op would otherwise leave such a slot
 * EMPTY forever whenever the fingerprint inputs (coords/intent/documents) are
 * unchanged — so the dirty-set computer must treat it as DIRTY.
 *
 * We deliberately gate on the presence of `status`: a slot WITHOUT a status is
 * not a compiled `EvidenceSection` (e.g. a sparse caller's stub), so we cannot
 * assert it is data-starved and leave its dirtiness to the fingerprint axes.
 */
function isProviderSlotDataStarved(
  sed: SiteEvidenceDocument | null | undefined,
  section: SedSectionId,
): boolean {
  if (!sed) return false;
  const slot = (sed as unknown as Record<string, unknown>)[section] as
    | { status?: unknown; data?: unknown }
    | undefined;
  // Only inspect a real compiled EvidenceSection (carries a string status).
  if (!slot || typeof slot.status !== 'string') return false;
  if (slot.status === 'unavailable') return true;
  const data = slot.data;
  if (data == null) return true;
  if (Array.isArray(data) && data.length === 0) return true;
  return false;
}

/**
 * Compute the per-section `inputHash`. Pure. The `priorHashes` map carries the
 * upstream sections' just-computed inputHashes so the transitive axis is
 * included; when an upstream hash isn't available yet it is treated as UNKNOWN
 * (conservative — forces the section dirty until upstream is fingerprinted).
 */
export function computeSectionInputHash(
  section: SedSectionId,
  deps: SedSectionDeps,
  inputs: SedFingerprintInputs,
  sed: SiteEvidenceDocument | null | undefined,
  priorHashes: ReadonlyMap<SedSectionId, string>,
): string {
  const parts: string[] = [`v:${EXTRACTION_MODEL_VERSION}`, `sec:${section}`];

  // ── Provider-input bundle: coords + per-provider config ──
  // Coords are the spatial key every live provider keys on.
  const lat = inputs.coords?.lat;
  const lng = inputs.coords?.lng;
  const coordsKey =
    typeof lat === 'number' && Number.isFinite(lat) &&
    typeof lng === 'number' && Number.isFinite(lng)
      ? `${lat.toFixed(6)},${lng.toFixed(6)}`
      : UNKNOWN; // no coords ⇒ unknown spatial key ⇒ conservative dirty
  for (const providerId of deps.providers) {
    const cfg = inputs.providerConfig
      ? inputs.providerConfig[providerId]
      : undefined;
    // providerConfig absent entirely ⇒ treat as 'no extra config' (stable);
    // present-but-this-provider-missing ⇒ also 'no extra config'. The coords
    // axis still keys it. We do NOT emit UNKNOWN for a missing config blob —
    // config is genuinely optional and absence is a stable state.
    parts.push(`p:${providerId}@${coordsKey}#${stableSerialize(cfg)}`);
  }
  // A providerless section still keys on coords so a site MOVE dirties it.
  if (deps.providers.length === 0 && (deps.intentFields.length > 0 || deps.docRequirements.length > 0)) {
    parts.push(`geo:${coordsKey}`);
  }

  // ── Linked doc contentHashes ──
  if (deps.docRequirements.length > 0) {
    const linkedIds = linkedDocIdsForSection(sed, section);
    const byId = new Map<string, SiteDocument>();
    for (const d of inputs.documents ?? []) byId.set(d.id, d);
    for (const id of linkedIds) {
      const docu = byId.get(id);
      if (!docu) {
        // Linked id present on the SED but the doc isn't in the live set we
        // were handed ⇒ we cannot hash its bytes ⇒ conservative dirty.
        parts.push(`d:${id}=${UNKNOWN}`);
        continue;
      }
      // Prefer the byte-level contentHash; fall back to a derived signature
      // (uploadedAt + size) so a doc without a stamped hash still participates
      // rather than silently dropping (which would risk under-recompute).
      const hash = docu.contentHash
        ?? `noh:${(docu as { uploadedAt?: string }).uploadedAt ?? ''}:${(docu as { fileSize?: number }).fileSize ?? ''}`;
      parts.push(`d:${id}=${hash}`);
    }
  }

  // ── Intent fields ──
  for (const field of deps.intentFields) {
    const val = readIntentField(inputs.intent, field);
    parts.push(`i:${field}=${stableSerialize(val)}`);
  }

  if (section === 'economicEvidence') {
    parts.push(`economic:${stableSerialize(inputs.economicEvidenceRevision ?? UNKNOWN)}`);
  }

  // ── Upstream sections' inputHashes (transitive axis) ──
  for (const up of deps.upstreamSections) {
    const upHash = priorHashes.get(up);
    parts.push(`u:${up}=${upHash ?? UNKNOWN}`);
  }

  return fnv1a(parts.join('|'));
}

/**
 * Compute the full per-section inputHash map for the live inputs. Visits
 * sections in topological order (upstream before downstream) so the transitive
 * axis sees already-computed upstream hashes. The graph is acyclic
 * (test-enforced) so a Kahn-style order always exists; we fall back to a fixed
 * iteration if a cycle ever slips through (every upstream then reads UNKNOWN →
 * still conservative, never wrong).
 */
export function computeAllSectionHashes(
  inputs: SedFingerprintInputs,
  sed: SiteEvidenceDocument | null | undefined,
): Map<SedSectionId, string> {
  const order = topoOrder();
  const hashes = new Map<SedSectionId, string>();
  for (const section of order) {
    const deps = SED_DEPENDENCY_GRAPH[section];
    hashes.set(section, computeSectionInputHash(section, deps, inputs, sed, hashes));
  }
  return hashes;
}

/** Kahn topological order over the upstream edges (upstream first). */
function topoOrder(): SedSectionId[] {
  const indeg = new Map<SedSectionId, number>();
  for (const id of SED_SECTION_IDS) indeg.set(id, 0);
  // An edge S → up means S depends on up; in-degree counts dependencies.
  for (const id of SED_SECTION_IDS) {
    indeg.set(id, SED_DEPENDENCY_GRAPH[id].upstreamSections.length);
  }
  const queue: SedSectionId[] = [];
  for (const id of SED_SECTION_IDS) if ((indeg.get(id) ?? 0) === 0) queue.push(id);
  const out: SedSectionId[] = [];
  while (queue.length > 0) {
    const node = queue.shift()!;
    out.push(node);
    for (const dependent of SED_DOWNSTREAM_DEPENDENTS[node]) {
      const d = (indeg.get(dependent) ?? 0) - 1;
      indeg.set(dependent, d);
      if (d === 0) queue.push(dependent);
    }
  }
  // Cycle fallback — append any unvisited so the caller still hashes them.
  if (out.length < SED_SECTION_IDS.length) {
    for (const id of SED_SECTION_IDS) if (!out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Stamp the computed fingerprints onto a SED (mutates a shallow copy of the
 * `sectionFingerprints` map; the rest of the SED is untouched). Returns the
 * SED for chaining. Call on EVERY compile so the next rebuild can go
 * incremental.
 */
export function stampSedFingerprints(
  sed: SiteEvidenceDocument,
  inputs: SedFingerprintInputs,
  now: () => string = () => new Date().toISOString(),
): SiteEvidenceDocument {
  const hashes = computeAllSectionHashes(inputs, sed);
  const computedAt = now();
  const stamped: Partial<Record<SedSectionId, SectionFingerprint>> = {};
  for (const [section, inputHash] of hashes) {
    stamped[section] = {
      inputHash,
      computedAt,
      modelVersion: EXTRACTION_MODEL_VERSION,
    };
  }
  sed.sectionFingerprints = stamped;
  return sed;
}

/**
 * Cost Phase P4 — stamp fingerprints for ONLY the genuinely-recomputed
 * sections, carrying over the PRIOR fingerprint verbatim for every other
 * section. This is the enforcement point for the cardinal invariant: a
 * section's fingerprint is stamped CURRENT only when it was actually recomputed
 * this run. A section NOT in `recomputedSections` keeps its prior fingerprint
 * exactly (so a dirty-but-not-recomputed section can never end up stamped
 * current — the incremental orchestrator forbids that case by forcing a full
 * rebuild, but this carry-over is the belt-and-braces guarantee).
 *
 * `priorSed` supplies the carried-over fingerprints. When a recomputed section
 * had no prior fingerprint that's fine — it gets a fresh one here.
 *
 * Mutates `sed.sectionFingerprints` and returns `sed` for chaining.
 */
export function stampSectionFingerprints(
  sed: SiteEvidenceDocument,
  inputs: SedFingerprintInputs,
  recomputedSections: ReadonlyArray<SedSectionId>,
  priorSed: SiteEvidenceDocument | null | undefined,
  now: () => string = () => new Date().toISOString(),
): SiteEvidenceDocument {
  const recompute = new Set<SedSectionId>(recomputedSections);
  // Hash over the live inputs against the just-merged SED (so linked-doc /
  // upstream axes read the merged state). Only the recomputed sections consume
  // these fresh hashes; the rest carry their prior fingerprint.
  const liveHashes = computeAllSectionHashes(inputs, sed);
  const computedAt = now();
  const priorFingerprints = priorSed?.sectionFingerprints ?? sed.sectionFingerprints;
  const stamped: Partial<Record<SedSectionId, SectionFingerprint>> = {};

  for (const section of SED_SECTION_IDS) {
    if (recompute.has(section)) {
      stamped[section] = {
        inputHash: liveHashes.get(section) ?? '',
        computedAt,
        modelVersion: EXTRACTION_MODEL_VERSION,
      };
    } else {
      // NOT recomputed ⇒ carry the prior fingerprint verbatim. Never stamp a
      // non-recomputed section with a fresh (current-input) hash — that is the
      // exact stale-but-stamped-current bug this whole module prevents.
      const prior = priorFingerprints?.[section];
      if (prior) stamped[section] = prior;
      // If a non-recomputed section has NO prior fingerprint, leave it absent
      // so the NEXT dirty-set computation marks it dirty (conservative).
    }
  }

  sed.sectionFingerprints = stamped;
  return sed;
}

/* ── Dirty set ─────────────────────────────────────────────────────────── */

export type DirtyCause =
  | 'no-prior-fingerprint'
  | 'input-hash-changed'
  | 'model-version-drift'
  | 'transitive-upstream'
  | 'unknown-input'
  /**
   * The data-starvation guard: a provider-fed section whose stamped fingerprint
   * is CURRENT (inputs unchanged) but whose slot `.data` is null/empty /
   * `unavailable`. Without this the incremental no-op leaves empty provider
   * slots permanently empty and the report renders data-starved.
   */
  | 'empty-provider-slot';

export interface SedDirtySet {
  /** Sections whose inputs changed (incl. transitive). Empty ⇒ nothing to do. */
  dirtySections: SedSectionId[];
  /** Per-section reason (for telemetry + the audit log). */
  reasons: Record<string, DirtyCause>;
  /** Total section count (denominator for the >70% fallback). */
  totalSections: number;
}

export interface ComputeDirtySetResult extends SedDirtySet {
  /**
   * When true, the caller MUST do a FULL rebuild instead of an incremental
   * one (strategy §2e fallbacks): no prior fingerprints at all (legacy
   * backfill), a global EXTRACTION_MODEL_VERSION bump, or >70% of sections
   * dirty.
   */
  forceFull: boolean;
  /** Which fallback fired (when `forceFull`), for the audit log. */
  fullReason?: 'no-fingerprints' | 'model-version-bump' | 'over-threshold';
}

/** Fraction-of-sections-dirty threshold above which a full rebuild wins. */
export const FULL_REBUILD_DIRTY_FRACTION = 0.7;

/**
 * Compute the CONSERVATIVE dirty set for the live inputs against the prior
 * SED's stamped fingerprints.
 *
 * Conservatism guarantees (the cardinal rule — never under-recompute):
 *   - A section with NO prior fingerprint ⇒ DIRTY (cause 'no-prior-fingerprint').
 *   - A section whose live hash contains an UNKNOWN axis differs from any
 *     stamped hash ⇒ DIRTY (the hash itself encodes the uncertainty).
 *   - A section whose stamped modelVersion ≠ current ⇒ DIRTY
 *     (cause 'model-version-drift').
 *   - A changed hash ⇒ DIRTY (cause 'input-hash-changed').
 *   - Transitive closure over `upstreamSections` ⇒ DIRTY
 *     (cause 'transitive-upstream').
 *
 * Full-rebuild fallbacks (strategy §2e):
 *   - prior SED has NO `sectionFingerprints` at all ⇒ forceFull
 *     (legacy backfill — the full rebuild stamps them).
 *   - the prior SED's fingerprints were all written under a DIFFERENT
 *     model version (global bump) ⇒ forceFull.
 *   - dirty fraction > 70% ⇒ forceFull (incremental overhead not worth it).
 */
export function computeSedDirtySet(
  prevSed: SiteEvidenceDocument | null | undefined,
  inputs: SedFingerprintInputs,
): ComputeDirtySetResult {
  const total = SED_SECTION_IDS.length;
  const reasons: Record<string, DirtyCause> = {};

  const priorFingerprints = prevSed?.sectionFingerprints;

  // Fallback 1 — no prior fingerprints at all (legacy SED / first run).
  if (!priorFingerprints || Object.keys(priorFingerprints).length === 0) {
    const all = [...SED_SECTION_IDS];
    for (const s of all) reasons[s] = 'no-prior-fingerprint';
    return {
      dirtySections: all,
      reasons,
      totalSections: total,
      forceFull: true,
      fullReason: 'no-fingerprints',
    };
  }

  // Fallback 2 — global EXTRACTION_MODEL_VERSION bump: every stamped
  // fingerprint predates the current version. If NONE of the prior
  // fingerprints match the current model version, the extraction prompt /
  // schema changed under the whole SED → full re-extract.
  const anyCurrentVersion = Object.values(priorFingerprints).some(
    (fp) => fp?.modelVersion === EXTRACTION_MODEL_VERSION,
  );
  if (!anyCurrentVersion) {
    const all = [...SED_SECTION_IDS];
    for (const s of all) reasons[s] = 'model-version-drift';
    return {
      dirtySections: all,
      reasons,
      totalSections: total,
      forceFull: true,
      fullReason: 'model-version-bump',
    };
  }

  // ── Per-section own-input comparison ──
  const liveHashes = computeAllSectionHashes(inputs, prevSed);
  const dirty = new Set<SedSectionId>();

  for (const section of SED_SECTION_IDS) {
    // ── Data-starvation guard (runs FIRST, independent of the fingerprint) ──
    // A provider-fed section whose slot is empty/unavailable must be DIRTY even
    // when its inputs are unchanged — otherwise the incremental "topup-no-
    // upgrades" no-op leaves empty provider slots (climate/grid/solar/
    // electricity …) permanently empty and the report renders data-starved.
    // This is the one case where a CURRENT fingerprint must NOT short-circuit
    // to clean.
    if (isProviderFedSection(section) && isProviderSlotDataStarved(prevSed, section)) {
      dirty.add(section);
      reasons[section] = 'empty-provider-slot';
      continue;
    }

    const prior = priorFingerprints[section];
    if (!prior) {
      dirty.add(section);
      reasons[section] = 'no-prior-fingerprint';
      continue;
    }
    if (prior.modelVersion !== EXTRACTION_MODEL_VERSION) {
      dirty.add(section);
      reasons[section] = 'model-version-drift';
      continue;
    }
    const live = liveHashes.get(section);
    if (live == null) {
      // Should not happen (we hashed every section), but if it did, be safe.
      dirty.add(section);
      reasons[section] = 'unknown-input';
      continue;
    }
    if (live !== prior.inputHash) {
      dirty.add(section);
      reasons[section] = 'input-hash-changed';
    }
  }

  // ── Transitive closure: a dirty upstream dirties its dependents (BFS) ──
  const queue: SedSectionId[] = [...dirty];
  while (queue.length > 0) {
    const node = queue.shift()!;
    for (const dependent of SED_DOWNSTREAM_DEPENDENTS[node]) {
      if (!dirty.has(dependent)) {
        dirty.add(dependent);
        reasons[dependent] = 'transitive-upstream';
        queue.push(dependent);
      }
    }
  }

  const dirtySections = SED_SECTION_IDS.filter((s) => dirty.has(s));

  // Fallback 3 — >70% dirty: incremental overhead isn't worth it.
  const overThreshold = dirtySections.length > total * FULL_REBUILD_DIRTY_FRACTION;

  return {
    dirtySections,
    reasons,
    totalSections: total,
    forceFull: overThreshold,
    fullReason: overThreshold ? 'over-threshold' : undefined,
  };
}

/* ── Cost P5 — SED dirty set SINCE a prior report ─────────────────────────
 *
 * For incremental REPORTS (not the SED rebuild), the relevant question is
 * "which SED sections changed since the PARENT REPORT was generated?" — not
 * "since the last SED compile." The parent report pins a snapshot of the SED's
 * section fingerprints at gen time (`sedSectionFingerprintsAtGen`); the live SED
 * carries its current `sectionFingerprints`. Diffing the two yields the SED
 * dirty set the report stale-set computer consumes.
 *
 * SAME CONSERVATISM as `computeSedDirtySet`:
 *   - the parent has NO fingerprint snapshot (legacy report) ⇒ forceFull,
 *   - the LIVE SED has no fingerprints (legacy SED) ⇒ forceFull,
 *   - a section present live but absent in the snapshot ⇒ DIRTY,
 *   - a section whose hash differs ⇒ DIRTY,
 *   - a model-version drift on either side ⇒ DIRTY,
 *   - transitive closure over upstream edges ⇒ DIRTY.
 * Never under-mark: when in doubt the section is dirty (or we forceFull).
 */
export function computeSedDirtySetSinceReport(
  parentFingerprints:
    | Partial<Record<SedSectionId, SectionFingerprint>>
    | null
    | undefined,
  liveSed: SiteEvidenceDocument | null | undefined,
): ComputeDirtySetResult {
  const total = SED_SECTION_IDS.length;
  const reasons: Record<string, DirtyCause> = {};
  const liveFingerprints = liveSed?.sectionFingerprints;

  // Fallback 1 — parent never stamped a snapshot (pre-P5 report) OR the live
  // SED carries no fingerprints (legacy / never compiled with P4). Either way
  // we cannot scope ⇒ full regen.
  const parentEmpty = !parentFingerprints || Object.keys(parentFingerprints).length === 0;
  const liveEmpty = !liveFingerprints || Object.keys(liveFingerprints).length === 0;
  if (parentEmpty || liveEmpty) {
    const all = [...SED_SECTION_IDS];
    for (const s of all) reasons[s] = 'no-prior-fingerprint';
    return {
      dirtySections: all,
      reasons,
      totalSections: total,
      forceFull: true,
      fullReason: 'no-fingerprints',
    };
  }

  const dirty = new Set<SedSectionId>();
  for (const section of SED_SECTION_IDS) {
    const prior = parentFingerprints![section];
    const live = liveFingerprints![section];
    if (!prior || !live) {
      // One side lacks this section's fingerprint ⇒ cannot prove unchanged.
      dirty.add(section);
      reasons[section] = 'no-prior-fingerprint';
      continue;
    }
    if (prior.modelVersion !== live.modelVersion) {
      dirty.add(section);
      reasons[section] = 'model-version-drift';
      continue;
    }
    if (prior.inputHash !== live.inputHash) {
      dirty.add(section);
      reasons[section] = 'input-hash-changed';
    }
  }

  // Transitive closure: a dirty upstream dirties its dependents (BFS).
  const queue: SedSectionId[] = [...dirty];
  while (queue.length > 0) {
    const node = queue.shift()!;
    for (const dependent of SED_DOWNSTREAM_DEPENDENTS[node]) {
      if (!dirty.has(dependent)) {
        dirty.add(dependent);
        reasons[dependent] = 'transitive-upstream';
        queue.push(dependent);
      }
    }
  }

  const dirtySections = SED_SECTION_IDS.filter((s) => dirty.has(s));
  const overThreshold = dirtySections.length > total * FULL_REBUILD_DIRTY_FRACTION;
  return {
    dirtySections,
    reasons,
    totalSections: total,
    forceFull: overThreshold,
    fullReason: overThreshold ? 'over-threshold' : undefined,
  };
}
